"use strict";

import Vue from "vue";
import axios from "axios";
import firebase from "firebase/app";
import "firebase/auth";
import store from "../store";
import { authReady } from "../firebase";

let config = {
  baseURL: process.env.VUE_APP_API_URL || "http://localhost",
  withCredentials: false
};

let configRoutingRules = {
  baseURL: process.env.VUE_APP_ROUTING_RULES_API || "http://localhost:8080",
  withCredentials: false
};

let configContractService = {
  baseURL: process.env.VUE_APP_CONTRACT_SERVICE_API
};

let configOrganisationService = {
  baseURL: process.env.VUE_APP_ORGANISATION_SERVICE_API
};

const _axios = axios.create(config);

const _axiosRoutingRules = axios.create(configRoutingRules);

const _axiosContractService = axios.create(configContractService);

const _axiosOrganisationService = axios.create(configOrganisationService);

_axios.interceptors.request.use(
  async config => {
    await authReady;
    const user = firebase.auth().currentUser;
    if (user) {
      // Returns the cached token and refreshes it when expired
      config.headers.Authorization = `Bearer ${await user.getIdToken()}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

let tokenRefresh = null;

_axios.interceptors.response.use(
  response => response,
  async error => {
    const status = error.response && error.response.status;
    const config = error.config;

    // The middleware answers 403 for invalid/expired tokens, but also for real
    // permission errors. Refresh the token once and retry to tell them apart.
    if ((status === 401 || status === 403) && config && !config._retried) {
      const user = firebase.auth().currentUser;
      if (!user) {
        store.dispatch("application/logout");
        return Promise.reject(error);
      }

      config._retried = true;
      try {
        tokenRefresh = tokenRefresh || user.getIdToken(true);
        await tokenRefresh;
      } catch (e) {
        // Refresh token revoked or expired: the session is gone
        store.dispatch("application/logout");
        return Promise.reject(error);
      } finally {
        tokenRefresh = null;
      }
      return _axios(config);
    }

    return Promise.reject(error);
  }
);

Plugin.install = function(_Vue) {
  _Vue.$axios = _axios;
  _Vue.$axiosRoutingRules = _axiosRoutingRules;
  _Vue.$axiosContractService = _axiosContractService;
  _Vue.$axiosOrganisationService = _axiosOrganisationService;

  window.axios = _axios;
  window.axiosRoutingRules = _axiosRoutingRules;
  window.axiosContractService = _axiosContractService;
  window.axiosOrganisationService = _axiosOrganisationService;

  Object.defineProperties(_Vue.prototype, {
    axios: {
      get() {
        return _axios;
      }
    },
    axiosRoutingRules: {
      get() {
        return _axiosRoutingRules;
      }
    },
    axiosContractService: {
      get() {
        return _axiosContractService;
      }
    },
    axiosOrganisationService: {
      get() {
        return _axiosOrganisationService;
      }
    },
    $axios: {
      get() {
        return _axios;
      }
    },
    $axiosRoutingRules: {
      get() {
        return _axiosRoutingRules;
      }
    },
    $axiosContractService: {
      get() {
        return _axiosContractService;
      }
    },
    $axiosOrganisationService: {
      get() {
        return _axiosOrganisationService;
      }
    }
  });
};

Vue.use(Plugin);

export default Plugin;
